import React from "react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import { LeadModalProvider } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import { useLeadModal } from "@/components/stori/LeadModalContext";
import {
  ArrowRight,
  Crown,
  TrendingUp,
  Mail,
  Target,
  Clock,
  Wifi,
  ChartLine,
  Zap,
  Sparkles,
} from "lucide-react";
import SectionHeading from "@/components/stori/SectionHeading";
import VideoGallery from "@/components/stori/VideoGallery";
import Seo from "@/seo/Seo";

// --------------------------------------------------------------------------
// Hero
// --------------------------------------------------------------------------
function EventsHero() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="events-hero"
      className="relative w-full min-h-[80svh] overflow-hidden flex flex-col items-start justify-center pt-32 pb-24 border-b border-stori-border"
    >
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=2400&q=80"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 45%", filter: "saturate(0.95) contrast(1.1)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stori-bg via-stori-bg/85 to-stori-bg/30" />
        <div className="absolute inset-y-0 left-0 w-[40%] bg-stori-bg" />
        <div className="absolute inset-y-0 left-[40%] w-[18%] bg-gradient-to-r from-stori-bg to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-stori-bg to-transparent" />
        <div className="absolute -top-20 -left-20 w-[600px] h-[600px] bg-stori-cyan/12 rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 max-w-[1400px] w-full mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-6">
            <span className="inline-block h-px w-10 bg-stori-cyan" />
            FOR EVENT MARKETERS · AGENCIES · BRAND LAUNCHES
          </div>
          <h1
            data-testid="events-headline"
            className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white"
          >
            <span className="block fade-up">Turn every activation into</span>
            <span className="block fade-up text-stori-cyan cyan-text-glow" style={{ animationDelay: "120ms" }}>
              measurable revenue.
            </span>
          </h1>
          <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl leading-relaxed">
            Built for experiential agencies, in-house event teams, and brand launches that
            demand attribution, qualified leads, and influencer reach — not just photos.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openModal("trial_signup")}
              data-testid="events-cta-software"
              className="group inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
            >
              Get the software-only plan
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <button
              type="button"
              onClick={() => openModal("demo_request")}
              data-testid="events-cta-live-team"
              className="group inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan font-semibold px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors"
            >
              Book our live events team
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
          <p className="mt-5 text-sm lg:text-base text-stori-muted">
            We can be live at your venue in under 60 minutes.
          </p>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Stats row
// --------------------------------------------------------------------------
const STATS = [
  { value: "3.5x", label: "more qualified leads vs. traditional booth" },
  { value: "28%", label: "of guests flagged as influencers" },
  { value: "87%", label: "avg. email capture rate" },
  { value: "$2.1M", label: "avg. attributed revenue · 90 days" },
];

function Stats() {
  return (
    <section
      data-testid="events-stats"
      className="relative border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div
              key={i}
              data-testid={`events-stat-${i}`}
              className="lg:border-l lg:border-stori-border lg:first:border-l-0 lg:pl-8 lg:first:pl-0"
            >
              <div className="font-heading text-5xl lg:text-6xl font-semibold text-stori-cyan cyan-text-glow tracking-tight leading-none">
                {s.value}
              </div>
              <div className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-stori-muted leading-relaxed">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// What you ship
// --------------------------------------------------------------------------
const SHIPS = [
  {
    icon: Crown,
    title: "Influencer auto-detection",
    desc: "Real-time flag for guests with social reach. VIP playbook fires immediately — partnership offer, exclusive content, gift kit.",
  },
  {
    icon: Mail,
    title: "Qualified lead capture",
    desc: "Every interaction is a contact record with consent, demographics, preferences. Synced to your CRM during the event.",
  },
  {
    icon: Target,
    title: "ROI attribution",
    desc: "Track revenue back to the activation. Per-lead cost, per-influencer reach, per-segment conversion — all in one dashboard.",
  },
  {
    icon: TrendingUp,
    title: "Algorithmic content",
    desc: "AI-generated branded photos engineered for sharing. 210% more posts than traditional booths — your activation pays for itself in earned media.",
  },
];

function WhatYouShip() {
  return (
    <section
      data-testid="events-ships"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="WHAT YOUR ACTIVATION SHIPS WITH"
          title="Four things every event walks away with."
          lead="Stori isn't a photo booth — it's a mobile lead-gen and influencer-detection platform that fits inside your activation footprint."
        />
        <div className="mt-16 grid sm:grid-cols-2 gap-4">
          {SHIPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                data-testid={`events-ship-${i}`}
                className="p-8 rounded-2xl border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors flex gap-5"
              >
                <div className="w-12 h-12 rounded-lg border border-stori-cyan text-stori-cyan bg-stori-cyan/5 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-semibold text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-stori-muted leading-relaxed">{s.desc}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Setup timeline
// --------------------------------------------------------------------------
const TIMELINE = [
  { icon: Clock, label: "Day -7", title: "Playbook design", desc: "Our team co-builds your guest segments + automated follow-up sequences." },
  { icon: Wifi, label: "Day -1", title: "Setup in <60 min", desc: "Hardware ships pre-configured. Plug in, calibrate to your brand, you're live." },
  { icon: Zap, label: "Day 0", title: "Activation live", desc: "Guests engage. AI classifies. Playbooks fire. Your team watches the dashboard." },
  { icon: ChartLine, label: "Day +1", title: "ROI report", desc: "Attribution dashboard shows leads, influencers, revenue — ready for your client deck." },
];

function Timeline() {
  return (
    <section
      data-testid="events-timeline"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="ACTIVATION TIMELINE"
          title="From kickoff to ROI report — four touchpoints."
        />
        <div className="mt-16 relative">
          {/* Vertical timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-stori-border md:left-1/2 md:-translate-x-1/2" />
          <ol className="space-y-10 lg:space-y-14">
            {TIMELINE.map((t, i) => {
              const Icon = t.icon;
              const right = i % 2 === 1;
              return (
                <li
                  key={i}
                  data-testid={`events-timeline-${i}`}
                  className={`relative md:grid md:grid-cols-2 md:gap-12 items-center ${right ? "md:flex-row-reverse" : ""}`}
                >
                  <div className={`relative pl-16 md:pl-0 ${right ? "md:order-2 md:pl-12" : "md:pr-12 md:text-right"}`}>
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-stori-cyan">
                      {t.label}
                    </div>
                    <h3 className="mt-2 font-heading text-2xl lg:text-3xl font-semibold text-white tracking-tight">
                      {t.title}
                    </h3>
                    <p className="mt-2 text-stori-muted leading-relaxed">{t.desc}</p>
                  </div>
                  {/* Marker */}
                  <span className={`absolute top-0 left-0 md:left-1/2 md:-translate-x-1/2 w-12 h-12 rounded-full border-2 border-stori-cyan bg-stori-bg flex items-center justify-center cyan-glow`}>
                    <Icon className="w-5 h-5 text-stori-cyan" />
                  </span>
                  <div className={right ? "md:order-1" : ""} />
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Featured case study
// --------------------------------------------------------------------------
function FeaturedCase() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="events-featured-case"
      className="relative py-24 lg:py-32 border-b border-stori-border"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="label-eyebrow text-stori-cyan flex items-center gap-2 mb-5">
            <span className="inline-block h-px w-8 bg-stori-cyan" />
            CASE STUDY · TECH LAUNCH
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-[1.1]">
            Tech brand launch · San Francisco · 90 days.
          </h2>
          <p className="mt-6 text-stori-muted leading-relaxed text-base lg:text-lg">
            A two-day product launch event with one Stori activation as the centerpiece.
            Result: more qualified leads than the previous five activations combined,
            and an attribution report that paid for the next year of events.
          </p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Metric v="847" l="qualified leads · est. 120" />
            <Metric v="340" l="influencers identified" />
            <Metric v="1,200+" l="playbooks triggered" />
            <Metric v="$2.1M" l="attributed revenue · 90d" />
          </div>
          <blockquote className="mt-10 text-xl text-white leading-snug font-medium">
            &ldquo;We didn&apos;t just capture photos. We captured relationships and revenue.&rdquo;
          </blockquote>
          <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-stori-muted">
            — Cole Reyes · Senior Event Marketer, VANTAGE
          </div>
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="events-case-cta"
            className="mt-10 inline-flex items-center gap-2 bg-stori-cyan text-black font-medium px-6 py-3.5 rounded-md hover:bg-stori-cyanHover transition-colors"
          >
            See it for your next activation
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="rounded-2xl overflow-hidden border border-stori-border">
          <img
            src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1400&q=80"
            alt="Tech launch activation"
            loading="lazy"
            className="w-full h-full object-cover aspect-[4/3]"
          />
        </div>
      </div>
    </section>
  );
}

function Metric({ v, l }) {
  return (
    <div>
      <div className="font-heading text-3xl font-semibold text-stori-cyan cyan-text-glow">{v}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted mt-1 leading-tight">
        {l}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// CTA banner
// --------------------------------------------------------------------------
function CTABanner() {
  const { openModal } = useLeadModal();
  return (
    <section
      data-testid="events-cta"
      className="relative py-28 lg:py-40 border-b border-stori-border overflow-hidden"
    >
      <div className="absolute inset-0 grid-noise opacity-40 radial-mask" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-stori-cyan/15 rounded-full blur-[120px]" />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          NEXT ACTIVATION
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h2 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-semibold text-white tracking-tight leading-[1.04]">
          Ship attribution at your
          <br />
          <span className="text-stori-cyan cyan-text-glow">next activation.</span>
        </h2>
        <p className="mt-8 text-lg text-stori-muted max-w-2xl mx-auto leading-relaxed">
          We can be live at your venue in under 60 minutes. Bring the brand —
          we'll bring the lead-gen, the influencer detection, and the ROI dashboard.
        </p>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => openModal("demo_request")}
            data-testid="events-cta-banner-demo"
            className="inline-flex items-center gap-2 bg-stori-cyan text-black font-semibold px-7 py-4 rounded-md hover:bg-stori-cyanHover transition-all hover:translate-y-[-1px]"
          >
            Book a demo
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => openModal("trial_signup")}
            data-testid="events-cta-banner-trial"
            className="inline-flex items-center gap-2 border border-stori-cyan/60 text-stori-cyan px-7 py-4 rounded-md hover:bg-stori-cyan/10 transition-colors font-medium"
          >
            <Sparkles className="w-4 h-4" />
            Try at your next event
          </button>
        </div>
      </div>
    </section>
  );
}

// --------------------------------------------------------------------------
// Page export
// --------------------------------------------------------------------------
export default function Events() {
  return (
    <LeadModalProvider>
      <Seo path="/events" />
      <main data-testid="events-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <EventsHero />
        <Stats />
        <VideoGallery />
        <WhatYouShip />
        <Timeline />
        <FeaturedCase />
        <CTABanner />
        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
